import type { ReactNode } from 'react';
import { cx } from 'react-twc';

// 파란 도움말 아이콘 (persona / auto-reply 헤더 pill 공용)
const QuestionCircleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9 7.0625C8.4825 7.0625 8.0625 7.4825 8.0625 8V8.08025C8.0625 8.22943 8.00324 8.37251 7.89775 8.478C7.79226 8.58349 7.64918 8.64275 7.5 8.64275C7.35082 8.64275 7.20774 8.58349 7.10225 8.478C6.99676 8.37251 6.9375 8.22943 6.9375 8.08025V8C6.9375 7.45299 7.1548 6.92839 7.54159 6.54159C7.92839 6.1548 8.45299 5.9375 9 5.9375H9.087C9.49048 5.93769 9.88423 6.06141 10.2153 6.29204C10.5464 6.52267 10.7989 6.84914 10.9389 7.22756C11.0789 7.60597 11.0996 8.01818 10.9984 8.40874C10.8971 8.79931 10.6787 9.14951 10.3725 9.41225L9.795 9.90725C9.72255 9.97015 9.66433 10.0478 9.62424 10.1349C9.58415 10.2221 9.5631 10.3168 9.5625 10.4128V10.8125C9.5625 10.9617 9.50324 11.1048 9.39775 11.2102C9.29226 11.3157 9.14918 11.375 9 11.375C8.85082 11.375 8.70774 11.3157 8.60225 11.2102C8.49676 11.1048 8.4375 10.9617 8.4375 10.8125V10.4128C8.4375 9.89 8.6655 9.3935 9.06225 9.05375L9.6405 8.55875C9.77244 8.44566 9.86658 8.29486 9.91023 8.12665C9.95389 7.95845 9.94497 7.78091 9.88468 7.61792C9.82438 7.45494 9.71561 7.31433 9.573 7.21503C9.43039 7.11573 9.26078 7.0625 9.087 7.0625H9ZM9 13.25C9.19891 13.25 9.38968 13.171 9.53033 13.0303C9.67098 12.8897 9.75 12.6989 9.75 12.5C9.75 12.3011 9.67098 12.1103 9.53033 11.9697C9.38968 11.829 9.19891 11.75 9 11.75C8.80109 11.75 8.61032 11.829 8.46967 11.9697C8.32902 12.1103 8.25 12.3011 8.25 12.5C8.25 12.6989 8.32902 12.8897 8.46967 13.0303C8.61032 13.171 8.80109 13.25 9 13.25Z"
      fill="#556477"
    />
    <path
      d="M2.4375 9.5C2.4375 7.75952 3.1289 6.09032 4.35961 4.85961C5.59032 3.6289 7.25952 2.9375 9 2.9375C10.7405 2.9375 12.4097 3.6289 13.6404 4.85961C14.8711 6.09032 15.5625 7.75952 15.5625 9.5C15.5625 11.2405 14.8711 12.9097 13.6404 14.1404C12.4097 15.3711 10.7405 16.0625 9 16.0625C7.25952 16.0625 5.59032 15.3711 4.35961 14.1404C3.1289 12.9097 2.4375 11.2405 2.4375 9.5ZM9 4.0625C8.28594 4.0625 7.57887 4.20314 6.91916 4.4764C6.25945 4.74966 5.66002 5.15019 5.15511 5.65511C4.65019 6.16003 4.24966 6.75945 3.9764 7.41916C3.70314 8.07887 3.5625 8.78594 3.5625 9.5C3.5625 10.2141 3.70314 10.9211 3.9764 11.5808C4.24966 12.2405 4.65019 12.84 5.15511 13.3449C5.66002 13.8498 6.25945 14.2503 6.91916 14.5236C7.57887 14.7969 8.28594 14.9375 9 14.9375C10.4421 14.9375 11.8252 14.3646 12.8449 13.3449C13.8646 12.3252 14.4375 10.9421 14.4375 9.5C14.4375 8.05789 13.8646 6.67484 12.8449 5.65511C11.8252 4.63538 10.4421 4.0625 9 4.0625Z"
      fill="#556477"
    />
  </svg>
);

const PILL_CLASS = cx(
  'hidden md:flex w-fit items-center gap-1 rounded-full bg-[#E5EEFA] text-[#556477]',
  'py-2 md:py-2.5 px-3.5 md:px-5 text-[14px] md:text-[16px] leading-5 md:leading-6',
);

type HelpPillProps = {
  children: ReactNode;
  className?: string;
  /** 넘기면 클릭 가능한 button, 없으면 정적 안내 chip(div)으로 렌더링 */
  onClick?: () => void;
  'data-gtm-id'?: string;
};

const HelpPill = ({ children, className, onClick, ...rest }: HelpPillProps) => {
  const content = (
    <>
      <QuestionCircleIcon className="size-4.5 md:size-6" />
      {children}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cx(PILL_CLASS, 'relative cursor-pointer *:pointer-events-none', className)}
        {...rest}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={cx(PILL_CLASS, className)} {...rest}>
      {content}
    </div>
  );
};

export default HelpPill;
